import { localizedStrings } from '@core/strings'
import { ITemplateEditorChecklistTemplateVersion } from '@core/entities/checklist/TemplateEditorChecklistTemplateVersion/ITemplateEditorChecklistTemplateVersion'
import { Entity } from '@core/entities/entity'
import { plainToInstance, Type } from 'class-transformer'
import {
  ChecklistRelationship,
  ChecklistTemplateEditorQuestionSet,
  TemplateEditorChecklistTemplateVersionPermissions,
  TemplateEditorChecklistTemplateVersionStatus,
} from '@core/entities/checklist/TemplateEditorChecklistTemplateVersion/types'
import { NotificationConfig } from '@core/entities/notifications/NotificationConfig'
import {
  TemplateFormWrapper,
  TemplateFormWrapperQuestionsType,
} from '@core/entities/template/TemplateFormWrapper'
import { TemplateForm } from '@core/entities/template/TemplateForm'
import { User } from '@core/entities/user'
import { TaskTemplate } from '@core/entities/task/TaskTemplate'
import { TemplateWarning } from '@core/entities/template/TemplateWarnings'
import { TemplateQuestion } from '@core/entities/template/TemplateQuestion'
import {
  ChecklistTemplateApproval,
  IChecklistTemplateApprovalRule,
} from '@core/entities/checklist/checklist-template-editor'
import { statuses } from '@core/theme/statuses'

export class TemplateEditorChecklistTemplateVersion extends Entity<ITemplateEditorChecklistTemplateVersion> {
  id: number

  organisationId: number

  projectId?: number

  organisationName: string

  name: string

  description: string

  templateId: number

  version: number

  createdOn: string

  modifiedOn: string

  @Type(() => User)
  createdBy: User

  @Type(() => User)
  modifiedBy: User

  status: TemplateEditorChecklistTemplateVersionStatus

  @Type(() => TemplateFormWrapper)
  templateQuestions: Array<TemplateFormWrapper>

  clonable: boolean

  requiresApproval: boolean

  creatorSelfApprove: boolean

  @Type(() => ChecklistTemplateApproval)
  approvals?: ChecklistTemplateApproval

  geoLocationInputType: 'WHAT_3_WORDS' | 'DRAW_ON_MAP'
  requireGeolocationForChecklist?: boolean

  ownedByRegisterTemplate?: any

  defaultInspectionsTaskTemplate?: TaskTemplate

  permittedActions: TemplateEditorChecklistTemplateVersionPermissions

  @Type(() => TemplateWarning)
  globalWarnings: TemplateWarning[]

  @Type(() => TemplateWarning)
  questionWarnings: TemplateWarning[]

  @Type(() => NotificationConfig)
  notifications: Array<NotificationConfig>

  relationships?: Array<ChecklistRelationship>

  public isActive(): boolean {
    return this.status === 'ACTIVE'
  }

  public isReadOnly(): boolean {
    return this.status !== 'DRAFT'
  }

  public static new(payload: unknown): TemplateEditorChecklistTemplateVersion {
    const entity = plainToInstance(
      TemplateEditorChecklistTemplateVersion,
      payload,
    )

    return entity
  }

  public getMainQuestions(): TemplateForm {
    return this.templateQuestions.find((templateQuestions) => {
      return templateQuestions.questionsType === 'MAIN_QUESTIONS'
    })?.checklistQuestions
  }

  public getQuestionSetOptions(
    approvalId?: string,
  ): ChecklistTemplateEditorQuestionSet[] {
    const questionSets: ChecklistTemplateEditorQuestionSet[] = []
    const approvals = this.approvals?.data ?? []

    const mainQuestions = this.getMainQuestions()
    if (mainQuestions) {
      questionSets.push({
        ...mainQuestions,
        name: localizedStrings.checklistQuestions,
        type: 'CHECKLIST_MAIN_QUESTIONS',
      })
    }

    const currentIndex = approvalId
      ? approvals.findIndex((approval) => approval.id === approvalId)
      : approvals.length
    const previousApprovals =
      currentIndex > -1 ? approvals.slice(0, currentIndex) : approvals

    previousApprovals.forEach((approval) => {
      questionSets.push({
        ...approval.approvalQuestions,
        name: approval.label ?? '',
        type: 'APPROVAL_QUESTIONS',
        approvalId: approval.id,
      })
    })

    // Only sets with a multiple-choice question can back an `IS` condition.
    return questionSets.filter((set) =>
      set.questions?.some((group) =>
        group?.data?.some(
          (question) => question.questionType === 'MULTIPLE_CHOICE',
        ),
      ),
    )
  }

  public getSelectedQuestionSet(
    questionSet?: IChecklistTemplateApprovalRule['questionSet'],
  ): TemplateForm {
    const targetType = questionSet?.type ?? 'CHECKLIST_MAIN_QUESTIONS'
    const match = this.getQuestionSetOptions()?.find((set) => {
      if (set.type === 'APPROVAL_QUESTIONS') {
        return (
          targetType === 'APPROVAL_QUESTIONS' &&
          set.approvalId === questionSet?.approvalId
        )
      }
      return set.type === targetType
    })
    return TemplateForm.new(match)
  }

  public static statusColor(
    status: TemplateEditorChecklistTemplateVersionStatus,
  ) {
    switch (status) {
      case 'ACTIVE':
        return statuses.active.color
      case 'ARCHIVED':
        return statuses.archived.color
      case 'DISCARDED':
        return statuses.discarded.color
      case 'DRAFT':
        return statuses.draft.color
      default:
        return 'default'
    }
  }

  public static statusText(
    status: TemplateEditorChecklistTemplateVersionStatus,
  ) {
    switch (status) {
      case 'ACTIVE':
        return statuses.active.label
      case 'ARCHIVED':
        return statuses.archived.label
      case 'DISCARDED':
        return statuses.discarded.label
      case 'DRAFT':
        return statuses.draft.label
      default:
        return status
    }
  }

  public getQuestionInfo(questionId: string) {
    const getInfo = (templateQuestions: TemplateForm, sourceText) => {
      const groupIndex = templateQuestions.questions.findIndex(
        (questionGroup) => questionGroup.data.find((q) => q.id === questionId),
      )
      const questionIndex = templateQuestions.questions[
        groupIndex
      ].data.findIndex((q) => q.id === questionId)
      return {
        source: sourceText,
        groupIndex,
        questionIndex,
        body: TemplateQuestion.getBodyFormattedText(
          templateQuestions.questions[groupIndex].data[questionIndex].body,
        ),
      }
    }

    // Iterate through all template question wrappers to find the question
    for (const templateWrapper of this.templateQuestions) {
      if (
        templateWrapper.checklistQuestions?.questions.find((questionGroup) =>
          questionGroup.data.find((q) => q.id === questionId),
        )
      ) {
        // Use questionsType as the source text, with fallback to generic label
        const sourceText = this.getQuestionsTypeLabel(
          templateWrapper.questionsType,
        )
        return getInfo(templateWrapper.checklistQuestions, sourceText)
      }
    }

    return null
  }

  private getQuestionsTypeLabel(
    questionsType: TemplateFormWrapperQuestionsType,
  ): string {
    // Map questionsType to appropriate localized strings
    switch (questionsType) {
      case 'MAIN_QUESTIONS':
        return localizedStrings.questions || 'Main Questions'
      default:
        return String(questionsType) || 'Questions'
    }
  }
}
